import type { FC } from "react";

const documentationHtml = `﻿<!DOCTYPE html>
<html lang="sv">
<head>
  <meta charset="UTF-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1.0" />
  <title>ADOne — Systemdokumentation</title>
  <style>
    :root {
      --primary: #1a3a5c;
      --accent: #e8722a;
      --light-bg: #f5f7fa;
      --border: #d0d7e3;
      --text: #2c2c2c;
      --muted: #6b7280;
      --green: #16a34a;
      --red: #dc2626;
      --blue-light: #eff6ff;
    }
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body {
      font-family: 'Segoe UI', Arial, sans-serif;
      color: var(--text);
      line-height: 1.65;
      background: #fff;
    }
    /* Cover page */
    .cover {
      background: linear-gradient(135deg, var(--primary) 0%, #2b5278 60%, var(--accent) 100%);
      color: #fff;
      padding: 80px 60px 60px;
      min-height: 300px;
      page-break-after: always;
    }
    .cover h1 { font-size: 2.8em; font-weight: 700; margin-bottom: 12px; }
    .cover .subtitle { font-size: 1.2em; opacity: 0.85; margin-bottom: 32px; }
    .cover .meta { font-size: 0.9em; opacity: 0.7; margin-top: 40px; }
    /* Layout */
    .container { max-width: 960px; margin: 0 auto; padding: 40px 40px; }
    /* TOC */
    .toc { background: var(--light-bg); border-left: 5px solid var(--accent); padding: 28px 36px; margin-bottom: 40px; border-radius: 4px; }
    .toc h2 { color: var(--primary); margin-bottom: 16px; font-size: 1.1em; text-transform: uppercase; letter-spacing: 0.08em; }
    .toc ol { padding-left: 20px; }
    .toc li { margin-bottom: 5px; }
    .toc a { color: var(--primary); text-decoration: none; }
    .toc a:hover { text-decoration: underline; }
    /* Sections */
    .section { margin-bottom: 56px; }
    h2.section-title {
      font-size: 1.6em;
      color: var(--primary);
      border-bottom: 3px solid var(--accent);
      padding-bottom: 8px;
      margin-bottom: 24px;
      scroll-margin-top: 20px;
    }
    h3.subsection {
      font-size: 1.15em;
      color: var(--primary);
      margin: 28px 0 10px;
      padding-left: 10px;
      border-left: 4px solid var(--border);
    }
    h4 { font-size: 1em; color: var(--muted); margin: 16px 0 6px; text-transform: uppercase; letter-spacing: 0.06em; font-size: 0.82em; }
    p { margin-bottom: 12px; }
    ul, ol { padding-left: 22px; margin-bottom: 12px; }
    li { margin-bottom: 5px; }
    /* Info boxes */
    .info-box {
      background: var(--blue-light);
      border: 1px solid #bfdbfe;
      border-radius: 6px;
      padding: 14px 18px;
      margin: 16px 0;
      font-size: 0.93em;
    }
    .warn-box {
      background: #fffbeb;
      border: 1px solid #fcd34d;
      border-radius: 6px;
      padding: 14px 18px;
      margin: 16px 0;
      font-size: 0.93em;
    }
    /* Tables */
    table {
      width: 100%;
      border-collapse: collapse;
      margin: 16px 0 24px;
      font-size: 0.9em;
    }
    thead th {
      background: var(--primary);
      color: #fff;
      padding: 9px 12px;
      text-align: left;
      font-weight: 600;
    }
    tbody tr:nth-child(even) { background: var(--light-bg); }
    tbody td { padding: 8px 12px; border-bottom: 1px solid var(--border); vertical-align: top; }
    /* Badges */
    .badge {
      display: inline-block;
      padding: 2px 9px;
      border-radius: 12px;
      font-size: 0.8em;
      font-weight: 600;
      margin: 2px;
    }
    .badge-blue   { background: #dbeafe; color: #1d4ed8; }
    .badge-orange { background: #ffedd5; color: #c2410c; }
    .badge-green  { background: #dcfce7; color: #166534; }
    .badge-gray   { background: #f3f4f6; color: #374151; }
    /* Flow diagram */
    .flow {
      display: flex;
      align-items: center;
      flex-wrap: wrap;
      gap: 0;
      margin: 20px 0;
    }
    .flow-step {
      background: var(--primary);
      color: #fff;
      padding: 10px 18px;
      border-radius: 4px;
      font-size: 0.88em;
      text-align: center;
      min-width: 100px;
    }
    .flow-arrow {
      font-size: 1.4em;
      color: var(--accent);
      padding: 0 6px;
    }
    /* Code mono */
    .mono { font-family: 'Consolas', monospace; font-size: 0.9em; background: var(--light-bg); padding: 1px 5px; border-radius: 3px; }
    /* Page break hint */
    .page-break { page-break-before: always; }
    /* Footer */
    .doc-footer {
      background: var(--light-bg);
      border-top: 3px solid var(--primary);
      padding: 24px 40px;
      font-size: 0.85em;
      color: var(--muted);
      text-align: center;
      margin-top: 60px;
    }
  </style>
</head>
<body>

<!-- COVER -->
<div class="cover">
  <h1>ADOne</h1>
  <div class="subtitle">Systemdokumentation &mdash; Orderhanteringssystem för fordonsdelar</div>
  <p style="opacity:0.75; font-size:0.95em;">AD Sverige &bull; Internt system &bull; adone.se</p>
  <div class="meta">
    Dokumentversion: 1.3 &bull; Uppdaterad: September 2026 &bull; Målgrupp: Produktägare &amp; Projektledare
  </div>
</div>

<!-- MAIN CONTENT -->
<div class="container">

  <!-- TABLE OF CONTENTS -->
  <div class="toc">
    <h2>Innehållsförteckning</h2>
    <ol>
      <li><a href="#overview">Systemöversikt och syfte</a></li>
      <li><a href="#clienttypes">Klienttyper &mdash; tre driftlägen</a></li>
      <li><a href="#login">Inloggning och behörighet</a></li>
      <li><a href="#orders">Orderhantering &mdash; hela flödet</a></li>
      <li><a href="#orderstatuses">Orderstatusar</a></li>
      <li><a href="#customers">Kunder</a></li>
      <li><a href="#suppliers">Leverantörer</a></li>
      <li><a href="#carbrands">Bilmärken och rabatter</a></li>
      <li><a href="#admin">Administratörsfunktioner</a></li>
      <li><a href="#articletemplate">Artikelmallar</a></li>
      <li><a href="#dashboard">Dashboard &mdash; startsida</a></li>
      <li><a href="#pricing">Prisberäkningslogik</a></li>
      <li><a href="#environments">Miljöer &mdash; dev, test och produktion</a></li>
      <li><a href="#api">Koppling till API och AX</a> &mdash; <a href="/admin/documentation-export" target="_top" style="color:#e8722a;font-size:0.88em;">↗ Exportflöde (detaljer)</a></li>
      <li><a href="#integrations">Externa integrationer</a></li>
      <li><a href="#datalagring">Datalagring och säkerhetsaspekter</a></li>
      <li><a href="#backend">Backend-arkitektur (teknisk översikt)</a></li>
    </ol>
  </div>


  <!-- 1. OVERVIEW -->
  <div class="section" id="overview">
    <h2 class="section-title">1. Systemöversikt och syfte</h2>
    <p>
      <strong>ADOne</strong> är ett internt webbaserat orderhanteringssystem för <strong>AD Sverige</strong>
      som hanterar hela livscykeln för beställningar av fordonsreservdelar &mdash; från att en order skapas
      till att den exporteras till affärssystemet <strong>Microsoft Dynamics AX</strong> (ERP).
    </p>
    <p>
      Systemet används av anställda internt och är inte tillgängligt för externa slutkunder.
      Det är åtkomligt på <strong>adone.se</strong> och kräver inloggning via AD Sverigeorganisationens
      Microsoft-konton (Azure Active Directory).
    </p>

    <h3 class="subsection">Huvudsakliga funktioner</h3>
    <ul>
      <li>Skapa, redigera och hantera orders för reservdelar</li>
      <li>Hantera kunder, leverantörer och bilmärken</li>
      <li>Prissättning med märkesspecifika rabatter (VSN% och SPN%)</li>
      <li>Exportera orders till Microsoft Dynamics AX</li>
      <li>Skicka prisförslag via e-post till PostNord</li>
      <li>Dashboard med statistik och egna tilldelade orders</li>
      <li>Hantera artikel- och artikelgruppsrabatter samt konfigurera orderstatusar, artikelmallar och inställningar</li>
      <li>Synkronisera orderstatusar manuellt från AX (för admins)</li>
    </ul>

    <h3 class="subsection">Teknisk plattform (sammanfattning)</h3>
    <table>
      <thead><tr><th>Komponent</th><th>Teknik/Verktyg</th></tr></thead>
      <tbody>
        <tr><td>Frontend (denna applikation)</td><td>React 19, TypeScript, Redux, React Router</td></tr>
        <tr><td>Backend API</td><td>.NET 9 / ASP.NET Core, REST API v1 &amp; v2</td></tr>
        <tr><td>Databas</td><td>SQL Server via Entity Framework Core</td></tr>
        <tr><td>Inloggning</td><td>Microsoft Azure Active Directory (Entra ID), JWT-tokens</td></tr>
        <tr><td>Affärssystem</td><td>Microsoft Dynamics AX via AX Communication (Service Bus/JSON från API, CSV/SFTP mellan integrationen och AX)</td></tr>
        <tr><td>Hosting</td><td>Azure App Service (frontend + API)</td></tr>
        <tr><td>Övervakning</td><td>Azure Monitor + OpenTelemetry (Application Insights)</td></tr>
        <tr><td>Dokumentlagring</td><td>SharePoint (via API-integration)</td></tr>
      </tbody>
    </table>
  </div>


  <!-- 2. CLIENT TYPES -->
  <div class="section" id="clienttypes">
    <h2 class="section-title">2. Klienttyper &mdash; tre driftlägen</h2>
    <p>
      Systemet har ett unikt koncept: <strong>tre klienttyper</strong> som styr hur hela gränssnittet,
      menyn, formulärfälten, tillgängliga statusar och prismodellen beter sig. En inloggad
      användare kan byta klienttyp med en väljare i navigationen.
    </p>

    <div class="warn-box">
      <strong>Viktigt:</strong> Under pågående orderarbete (när man tittar på eller redigerar en order)
      är klienttypsväljaren låst. Den låses upp automatiskt när man lämnar ordervyn.
    </div>

    <table>
      <thead><tr><th>Klienttyp</th><th>Internt värde</th><th>Beskrivning</th></tr></thead>
      <tbody>
        <tr>
          <td><span class="badge badge-blue">Skadeverkstad</span></td>
          <td><span class="mono">Standard (0)</span></td>
          <td>Standardläge. Använder VSN-prissättning (verkstadssäljpris minus rabatt).</td>
        </tr>
        <tr>
          <td><span class="badge badge-orange">AD Butik</span></td>
          <td><span class="mono">Catalog (1)</span></td>
          <td>AD:s egna butiker. Katalogläge. Har ett särskilt godkännandeflöde (approve/deny) för orders.</td>
        </tr>
        <tr>
          <td><span class="badge badge-green">Fleet</span></td>
          <td><span class="mono">Fleet (2)</span></td>
          <td>Fleet-kunder. Separat statuslista och e-postflöde mot PostNord.</td>
        </tr>
      </tbody>
    </table>

    <p>
      Klienttypen påverkar:
    </p>
    <ul>
      <li>Vilka <strong>orderstatusar</strong> som visas i menyn till vänster</li>
      <li>Vilka <strong>kunder och leverantörer</strong> som är valbara</li>
      <li>Hur <strong>priserna</strong> beräknas (VSN vs. SPN-rabatter)</li>
      <li>Vilka <strong>fält</strong> som visas på orderformuläret</li>
      <li>Om ordern kräver <strong>godkännande</strong> (approve/deny-flöde för AD Butik)</li>
      <li><strong>Temat</strong> (färgtemat i navigationen)</li>
    </ul>

    <div class="info-box">
      Vald klienttyp sparas per session i webbläsaren, vilket innebär att om man stänger och öppnar om fliken
      startar man med samma klienttyp.
    </div>
  </div>


  <!-- 3. LOGIN -->
  <div class="section" id="login">
    <h2 class="section-title">3. Inloggning och behörighet</h2>
    <p>
      ADOne använder <strong>Microsoft-inloggning (Azure AD / Entra ID)</strong>. Det finns ingen
      intern användarhantering &mdash; alla behörigheter hanteras via AD Sverigeorganisationen i Microsoft.
    </p>

    <h3 class="subsection">Inloggningsflöde</h3>
    <div class="flow">
      <div class="flow-step">Användaren öppnar adone.se</div>
      <div class="flow-arrow">→</div>
      <div class="flow-step">Omdirigeras till Microsoft-inloggning</div>
      <div class="flow-arrow">→</div>
      <div class="flow-step">Loggar in med AD Sverige-konto</div>
      <div class="flow-arrow">→</div>
      <div class="flow-step">Omdirigeras tillbaka till systemet</div>
      <div class="flow-arrow">→</div>
      <div class="flow-step">Dashboard visas</div>
    </div>

    <p>
      Systemet tar automatiskt tag i användarens namn (visningsnamn och förnamn) från Microsoft-kontot
      och visar det i navigationen. Utloggning sker via användarmenyn uppe till höger.
    </p>

    <div class="info-box">
      All kommunikation med backend-APIet sker med en tidsbegränsad åtkomsttoken som systemet
      hämtar automatiskt i bakgrunden. Användaren behöver alltså inte logga in på nytt vid varje API-anrop.
    </div>

    <div class="warn-box">
      Rolltilldelning sker <strong>inte</strong> i ADOne självt &mdash; det hanteras av IT/systemadministratör i Microsoft Entra ID (Azure AD).
    </div>
  </div>


  <!-- 4. ORDERS -->
  <div class="section page-break" id="orders">
    <h2 class="section-title">4. Orderhantering &mdash; hela flödet</h2>
    <p>
      Orders är kärnan i systemet. En order representerar en beställning av reservdelar till ett fordon.
    </p>

    <h3 class="subsection">4.1 Hur orders kommer in i systemet</h3>
    <p>Orders når ADOne på olika sätt beroende på klienttyp:</p>
    <table>
      <thead><tr><th>Klienttyp</th><th>Orderkälla</th><th>Hur det fungerar</th></tr></thead>
      <tbody>
        <tr>
          <td><span class="badge badge-blue">Skadeverkstad</span></td>
          <td>E-post till AD Sverige</td>
          <td>Verkstaden skickar en beställning via e-post. En <strong>robot</strong> läser e-posten automatiskt och skapar ordern i ADOne.</td>
        </tr>
        <tr>
          <td><span class="badge badge-orange">AD Butik</span></td>
          <td>Innesälj via katalogen</td>
          <td>Order läggs av innesäljare direkt i katalogen, som sedan skickar in ordern till ADOne.</td>
        </tr>
        <tr>
          <td><span class="badge badge-green">Fleet</span></td>
          <td>PostNord via katalogen</td>
          <td>PostNord skickar in orders via katalogen, som vidarebefordrar dem till ADOne.</td>
        </tr>
      </tbody>
    </table>

    <h3 class="subsection">4.2 Orderflödet i stort</h3>
    <div class="flow">
      <div class="flow-step">Order skapas</div>
      <div class="flow-arrow">→</div>
      <div class="flow-step">Tilldelas handläggare</div>
      <div class="flow-arrow">→</div>
      <div class="flow-step">Bearbetas (artiklar, priser)</div>
      <div class="flow-arrow">→</div>
      <div class="flow-step">Exporteras till AX (ERP)</div>
      <div class="flow-arrow">→</div>
      <div class="flow-step">Order slutförd</div>
    </div>
    <p style="margin-top:8px; font-size:0.9em; color:var(--muted);">
      För AD Butik (Katalog) tillkommer ett steg: ordern måste godkännas (approve) eller nekas (deny) innan export är möjlig.
    </p>

    <h3 class="subsection">4.3 Orderlista per status</h3>
    <p>
      Varje statuslänk öppnar en <strong>orderlista</strong> med följande funktioner:
    </p>
    <ul>
      <li><strong>Sökning:</strong> Fritext-sökning på ordernummer, registreringsnummer, kundnamn m.m.</li>
      <li><strong>Sortering:</strong> Klicka på kolumnrubriken för att sortera stigande/fallande.</li>
      <li><strong>Sidnumrering:</strong> 50 orders per sida.</li>
      <li><strong>Automatisk uppdatering:</strong> Orderlistan hämtas om automatiskt var femte sekund.</li>
      <li><strong>Statusbadge:</strong> Varje order visar om den är exporterad, godkänd eller nekad.</li>
    </ul>

    <h3 class="subsection">4.4 Skapa ny order</h3>
    <p>
      Klicka på <strong>"Skapa order"</strong> från en statuslista. Formuläret innehåller (beroende på
      klienttyp och orderstatus):
    </p>
    <table>
      <thead><tr><th>Fältgrupp</th><th>Exempel på fält</th></tr></thead>
      <tbody>
        <tr>
          <td>Fordonsinformation</td>
          <td>Registreringsnummer, VIN-nummer, KType-nummer, Motorkodbeteckning, Bilinformation (fritext), Bilmärke, Bilmodell, Odometer</td>
        </tr>
        <tr>
          <td>Ordernummer</td>
          <td>AO-nummer, Inköpsordernummer, Försäljningsordernummer, PostNord ID (externt order-ID)</td>
        </tr>
        <tr>
          <td>Kund &amp; Leverantör</td>
          <td>Kundval (dropdown), Leverantörval (dropdown)</td>
        </tr>
        <tr>
          <td>Ordertyp</td>
          <td>Väljs ur lista &mdash; påverkar standardrabatt</td>
        </tr>
        <tr>
          <td>Datum</td>
          <td>Inlämningsdatum, Önskat leveransdatum m.fl.</td>
        </tr>
        <tr>
          <td>Kommentarer</td>
          <td>Kommentar (externt synlig), Intern kommentar</td>
        </tr>
        <tr>
          <td>Artikelrader</td>
          <td>Artikelnummer, AD-ID, Beskrivning, Antal, Listpris, VSN-pris (verkstad), SPN-pris (butik), Moms</td>
        </tr>
      </tbody>
    </table>

    <div class="info-box">
      <strong>Artikelrader:</strong> Man kan lägga till valfritt antal artikelrader. För Fleet-orders erbjuder systemet
      <em>artikelmallar</em> som snabbval för vanliga produkter och tidsartiklar. När ett bilmärke väljs 
      beräknar systemet automatiskt VSN- och SPN-priser baserat på märkets rabatter.
    </div>

    <div class="warn-box">
      <strong>Dublettdetektering:</strong> Om ett PostNord ID redan finns på en annan order visas en varning
      (HTTP 409). Användaren kan välja att ändå spara ordern om det är avsiktligt.
    </div>

    <h3 class="subsection">4.5 Redigera order</h3>
    <p>Samma formulär som vid skapande, men förifyllt med befintliga data. Använd knappen "Redigera" i ordervyn.</p>

    <h3 class="subsection">4.6 Artikelregler per klienttyp</h3>
    <p>
      Reglerna för artikelrader på en order skiljer sig åt beroende på klienttyp. Tabellen nedan
      sammanfattar de viktigaste skillnaderna, inklusive vad som krävs för att en export till AX ska lyckas.
    </p>
    <table>
      <thead>
        <tr>
          <th>Regel</th>
          <th><span class="badge badge-blue">Skadeverkstad</span></th>
          <th><span class="badge badge-orange">AD Butik</span></th>
          <th><span class="badge badge-green">Fleet</span></th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td><strong>Kund obligatorisk</strong></td>
          <td>Ja</td>
          <td>Ja</td>
          <td>Ja</td>
        </tr>
        <tr>
          <td><strong>Leverantör per artikel</strong></td>
          <td>Ja &mdash; obligatoriskt</td>
          <td>Ja &mdash; obligatoriskt</td>
          <td>Nej (valfritt)</td>
        </tr>
        <tr>
          <td><strong>Priskrav för export till AX</strong></td>
          <td>Alla fyra priser måste vara &ge; 1:<br><span class="mono">SalesPrice</span>, <span class="mono">StoreSalesPrice</span>, <span class="mono">WorkshopSalesPrice</span>, <span class="mono">PurchasePrice</span></td>
          <td>Samma som Skadeverkstad</td>
          <td>Endast <span class="mono">SalesPrice</span> &ge; 1</td>
        </tr>
        <tr>
          <td><strong>Artikelmallar</strong></td>
          <td>Nej</td>
          <td>Nej</td>
          <td>Ja</td>
        </tr>
        <tr>
          <td><strong>ADI-berikningsdata (vid export)</strong></td>
          <td>Nej</td>
          <td>Ja &mdash; ItemId, ItemGroupId</td>
          <td>Ja &mdash; ItemId, ItemGroupId, lagerplats</td>
        </tr>
        <tr>
          <td><strong>Lagerkontroll via ADI</strong></td>
          <td>Nej</td>
          <td>Nej</td>
          <td>Ja &mdash; kontrolleras vid export</td>
        </tr>
      </tbody>
    </table>
    <div class="info-box">
      <strong>Prisfälten förklarade:</strong>
      <ul style="margin-top:6px;margin-bottom:0;">
        <li><span class="mono">SalesPrice</span> &mdash; Försäljningspris (SPN, butikspris)</li>
        <li><span class="mono">StoreSalesPrice</span> &mdash; Butikssäljpris</li>
        <li><span class="mono">WorkshopSalesPrice</span> &mdash; Verkstadssäljpris (VSN)</li>
        <li><span class="mono">PurchasePrice</span> &mdash; Inköpspris</li>
      </ul>
      Fleet-orders kräver bara att SalesPrice är ifyllt &mdash; de övriga priserna är valfria.
    </div>
    <div class="warn-box">
      <strong>Vanligt fel vid export:</strong> Om en Skadeverkstad- eller AD Butik-order inte kan exporteras
      är det oftast för att ett eller flera av de fyra priserna saknas (<span class="mono">&lt; 1</span>) på
      någon artikelrad. Kontrollera alla artikelpriser i ordervyn innan du försöker exportera igen.
    </div>
    <h3 class="subsection">4.7 Ordervyn &mdash; vad man ser</h3>
    <p>Ordervyn visar all information om en enskild order och ger tillgång till alla åtgärder:</p>

    <table>
      <thead><tr><th>Funktion</th><th>Beskrivning</th></tr></thead>
      <tbody>
        <tr>
          <td><strong>Tilldela / Frånta tilldelning</strong></td>
          <td>Kopplar ordern till den inloggade användaren. Visas på dashboarden om tilldelad.</td>
        </tr>
        <tr>
          <td><strong>Exportera till AX</strong></td>
          <td>Skickar ordern till Microsoft Dynamics AX (ERP). Kräver att alla valideringsregler är uppfyllda. Kan inte ångras direkt via ordervyn (kräver admin-åtgärd).</td>
        </tr>
        <tr>
          <td><strong>Prisförslag till PostNord</strong></td>
          <td>För Fleet-orders kan handläggaren förhandsgranska prisförslaget och skicka det till PostNord efter lagerkontroll. Vid omsändning visas en bekräftelse.</td>
        </tr>
        <tr>
          <td><strong>Verifikation som PDF</strong></td>
          <td>Förhandsgranska verifikationen och skicka PDF via e-post till kunden. Tidigare skickade verifikationer kan skickas igen efter bekräftelse. Om kunden saknar e-postadress visas en varning och utskick kan inte göras.</td>
        </tr>
        <tr>
          <td><strong>Lagerstatuskontroll</strong> <span class="badge badge-green">Fleet</span></td>
          <td>Kontrollerar lagerstatus per artikel direkt mot AX. Gäller enbart Fleet-orders.</td>
        </tr>
        <tr>
          <td><strong>Kundinformation</strong></td>
          <td>Knapp som visar kunddetaljer i ett popup-fönster.</td>
        </tr>
        <tr>
          <td><strong>Orderhistorik</strong></td>
          <td>Fällbart avsnitt som visar en komplett ändringslogg &mdash; vem som ändrade vad och när.</td>
        </tr>
        <tr>
          <td><strong>E-posthistorik</strong> <span class="badge badge-green">Fleet</span></td>
          <td>Fällbart avsnitt som listar alla e-postmeddelanden som skickats kopplat till ordern (ämne, mottagare, avsändare, tidsstämpel). Gäller enbart Fleet-orders.</td>
        </tr>
      </tbody>
    </table>

    <h3 class="subsection">4.8 Godkännande/Nekande <span class="badge badge-orange">AD Butik</span> <span class="badge badge-green">Fleet</span></h3>
    <p>
      För klienttyperna <strong>AD Butik (Catalog)</strong> och <strong>Fleet</strong> kan orders ha status "godkänd" eller "nekad".
      Detta styrs via orderstatus-konfigurationen och är en förutsättning för export.
      Vanliga statusar i detta flöde är t.ex. "Väntar på godkännande" → "Godkänd" → "Exporterad".
    </p>

    <h3 class="subsection">4.9 Exportera order till AX</h3>
    <p>
      Export är ett centralt steg &mdash; det är när ordern officiellt förs över till
      <strong>Microsoft Dynamics AX</strong> (affärssystemet). API:et mappar ordern till ett JSON-meddelande
      och skickar det via <strong>Azure Service Bus</strong> till AX Communication. AX Communication mappar ordern
      till CSV och överför filerna via SFTP till AX. För svar pollar AX Communication svarssökvägen på SFTP var
      tionde sekund, mappar svarsfilerna till JSON och skickar dem via en separat Service Bus-kö till API:et.
      API:ets OrderWorkerService tar emot svaret, uppdaterar ordern och dess artiklar med AX-nummer och sätter callbackstatus.
    </p>

    <div class="flow">
      <div class="flow-step">Handläggare klickar &ldquo;Exportera&rdquo;</div>
      <div class="flow-arrow">→</div>
      <div class="flow-step">API mappar ordern till JSON</div>
      <div class="flow-arrow">→</div>
      <div class="flow-step">JSON skickas till Azure Service Bus</div>
      <div class="flow-arrow">→</div>
      <div class="flow-step">AX Communication skapar CSV och lägger på SFTP</div>
      <div class="flow-arrow">→</div>
      <div class="flow-step">AX hämtar ordern och skapar svar</div>
      <div class="flow-arrow">→</div>
      <div class="flow-step">AX Communication pollar SFTP-svar var 10:e sekund</div>
      <div class="flow-arrow">→</div>
      <div class="flow-step">Svaret skickas till API:ets svarskö</div>
      <div class="flow-arrow">→</div>
      <div class="flow-step">API uppdaterar ordern med IO och FO</div>
    </div>

    <ul>
      <li>Exporten syns tydligt på ordern med en badge.</li>
      <li>När AX har bekräftat sätts ordern automatiskt till den &ldquo;AX-återkopplingsstatus&rdquo; som är konfigurerad på orderstatusen.</li>
      <li>Om en export behöver ångras (fel order) &mdash; det görs av en <strong>administratör</strong> via Admin-sektionen.</li>
      <li>Orderstatus-konfigurationen styr vilka statusar som tillåter export.</li>
    </ul>
    <div class="warn-box">
      <strong>Viktigt:</strong> Exporten till AX är asynkron. AX Communication letar efter svar på SFTP var tionde sekund.
      <span class="mono">Exported = true</span> betyder att API:et publicerat meddelandet till kön, inte att AX har bekräftat ordern.
      IO/FO och callbackstatus uppdateras separat när AX-svaret kommer tillbaka och behandlas av API:et.
      CSV-exporten från dashboarden är en separat rapportfunktion.
    </div>
    <div class="info-box">
      <strong>Order-ID:n i integrationsflödet:</strong> <span class="mono">FileIdentifier</span> sätts till
      <span class="mono">AONumber</span> för Standard, <span class="mono">ExternalOrderId</span> för AD Butik och
      <span class="mono">ClientExternalOrderId</span> för Fleet. <span class="mono">ExternalId</span> är i stället respektive
      ADOne-posts GUID och används när API:t matchar AX-svaret tillbaka till ordern, artikeln eller kunden.
    </div>

    <h3 class="subsection">4.10 CSV-export av orders</h3>
    <p>
      Från dashboarden kan man exportera ett datumintervall av orders som en <strong>CSV-fil</strong>
      (kommaseparerad). Välj start- och slutdatum och klicka "Exportera". Filen laddas ned automatiskt.
    </p>
  </div>


  <!-- 5. ORDER STATUSES -->
  <div class="section" id="orderstatuses">
    <h2 class="section-title">5. Orderstatusar</h2>
    <p>
      Orderstatusar styr hela orderflödet. Varje status representerar ett steg i processen
      (t.ex. "Mottagen", "Under arbete", "Exporterad").
    </p>
    <p>
      Statusarna visas som <strong>menyval</strong> och filtrerar orderlistan.
      Varje status är kopplad till en eller flera <strong>klienttyper</strong>.
    </p>

    <h3 class="subsection">Konfiguration per orderstatus (Admin)</h3>
    <p>Admins kan konfigurera varje status med:</p>
    <table>
      <thead><tr><th>Inställning</th><th>Beskrivning</th></tr></thead>
      <tbody>
        <tr><td>Namn</td><td>Det namn som visas i menyn och på ordern</td></tr>
        <tr><td>Sorteringsordning</td><td>Nummerordning för visning i menyn</td></tr>
        <tr><td>Visa i meny</td><td>Om statusen ska synas i navigationsmenyn</td></tr>
        <tr><td>Klienttyp(er)</td><td>Vilken/vilka klienttyper statusen gäller</td></tr>
        <tr><td>Möjliggör export</td><td>Om orders i denna status kan exporteras till AX</td></tr>
        <tr><td>AX-återkopplingsstatus</td><td>Vilken status ordern automatiskt sätts till efter lyckad AX-export</td></tr>
        <tr><td>Kräver katalog-godkännande</td><td>Om AD Butik-orders kräver godkännande</td></tr>
        <tr><td>Katalog-godkänd status</td><td>Vilken status ordern sätts till vid godkännande</td></tr>
        <tr><td>Katalog-nekad status</td><td>Vilken status ordern sätts till vid nekande</td></tr>
        <tr><td>Slutlig status</td><td>Markerar att ordern är i ett avslutande tillstånd</td></tr>
        <tr><td>Formulärfält (OrderPageType)</td><td>~30 inställningar som styr vilka fält som visas på orderformuläret för denna status</td></tr>
      </tbody>
    </table>
  </div>


  <!-- 6. CUSTOMERS -->
  <div class="section" id="customers">
    <h2 class="section-title">6. Kunder</h2>
    <p>
      Kundregistret innehåller alla kunder som kan kopplas till orders.
      Varje kund är kopplad till en specifik <strong>klienttyp</strong>.
    </p>

    <h3 class="subsection">Kundinformation</h3>
    <table>
      <thead><tr><th>Fält</th><th>Beskrivning</th></tr></thead>
      <tbody>
        <tr><td>Namn</td><td>Kundens namn</td></tr>
        <tr><td>AX externt ID</td><td>Kundnummer i Microsoft Dynamics AX</td></tr>
        <tr><td>Gatuadress, Postnummer, Stad</td><td>Postadress</td></tr>
        <tr><td>E-post</td><td>Kontakt-e-post</td></tr>
        <tr><td>Telefon</td><td>Telefonnummer</td></tr>
        <tr><td>Dokumentmapp</td><td>Referens till dokumentlagring</td></tr>
        <tr><td>Kommentar</td><td>Fritext-notering om kunden</td></tr>
        <tr><td>Klienttyp</td><td>Skadeverkstad / AD Butik / Fleet</td></tr>
      </tbody>
    </table>

    <h3 class="subsection">Funktioner</h3>
    <ul>
      <li><strong>Lista kunder:</strong> Sidnumrerad lista filtrerad på aktiv klienttyp. Sök på namn eller ID.</li>
      <li><strong>Visa kund:</strong> Alla detaljer, läsläge.</li>
      <li><strong>Skapa kund:</strong> Formulär med ovanstående fält.</li>
      <li><strong>Redigera kund:</strong> Uppdatera befintliga uppgifter.</li>
    </ul>
  </div>


  <!-- 7. SUPPLIERS -->
  <div class="section" id="suppliers">
    <h2 class="section-title">7. Leverantörer</h2>
    <p>
      Leverantörsregistret innehåller alla leverantörer som kan kopplas till orders.
      En leverantör kan vara aktiv för en eller flera klienttyper.
    </p>

    <h3 class="subsection">Leverantörsinformation</h3>
    <table>
      <thead><tr><th>Fält</th><th>Beskrivning</th></tr></thead>
      <tbody>
        <tr><td>Namn</td><td>Leverantörens namn</td></tr>
        <tr><td>AX externt ID</td><td>Leverantörsnummer i Microsoft Dynamics AX</td></tr>
        <tr><td>Varumärke (Brand)</td><td>Kopplat varumärke</td></tr>
        <tr><td>Procent (%)</td><td>Rabattandel</td></tr>
        <tr><td>Upphämtningsnamn &amp; Adress</td><td>Upphämtningsinformation</td></tr>
        <tr><td>Adress, E-post, Telefon</td><td>Kontaktuppgifter</td></tr>
        <tr><td>Valuta</td><td>Faktureringsvaluta</td></tr>
        <tr><td>Exkluderas från export</td><td>Om sant exporteras inte leverantören till AX</td></tr>
        <tr><td>Klienttyper</td><td>Aktiv för Skadeverkstad, AD Butik och/eller Fleet</td></tr>
        <tr><td>Lager (Warehouse)</td><td>Kopplat lager. Om Fleet är ikryssat sätts lagerstället till BES.</td></tr>
      </tbody>
    </table>

    <h3 class="subsection">Funktioner</h3>
    <ul>
      <li><strong>Lista leverantörer:</strong> Sidnumrerad lista med checkmarks per klienttyp.</li>
      <li><strong>Visa leverantör:</strong> Alla detaljer + ändringshistorik (vem ändrade vad och när).</li>
      <li><strong>Skapa / Redigera leverantör:</strong> CRUD-formulär.</li>
      <li>
        <strong>Importera leverantörer från AX:</strong> Knappen startar importen av leverantörsuppgifter från Microsoft Dynamics AX.
        Gränssnittet visar när importen har startat och anger att listan uppdateras när importen är klar.
      </li>
      <li><strong>Leverantörshistorik:</strong> Fällbar ändringslogg på varje leverantör.</li>
    </ul>

    <div class="info-box">
      <strong>AX-synkronisering:</strong> Leverantörsimport startas manuellt med knappen i listan. Om en leverantör är <em>exkluderad från export</em> syns den fortfarande
      i systemet men inkluderas inte när orders exporteras till AX. Om leverantören är markerad som Fleet sätts lagerstället till BES.
    </div>
  </div>


  <!-- 8. CAR BRANDS & DISCOUNTS -->
  <div class="section" id="carbrands">
    <h2 class="section-title">8. Bilmärken och märkesrabatter</h2>
    <div class="warn-box">
      <strong>Gäller enbart Catalog-orders (AD Butik):</strong> Hela sektionen för bilmärken och märkesrabatter används bara när klienttypen är inställd på <span class="badge badge-orange">AD Butik</span>. Funktionen är inte aktiv för Skadeverkstad eller Fleet.
    </div>

    <h3 class="subsection">Bilmärken</h3>
    <p>
      Register över bilmärken (t.ex. Volvo, BMW, Toyota). Dessa kopplas till orders och används
      för att beräkna märkesspecifika rabatter.
    </p>
    <ul>
      <li>Varje bilmärke har ett <strong>namn</strong>.</li>
      <li>Skapas, visas och redigeras via menyn "Bilmärken".</li>
    </ul>

    <h3 class="subsection">Märkesrabatter (Brand Discounts)</h3>
    <p>
      Varje bilmärke kan ha en kopplad <strong>märkesrabatt</strong> som definierar prissättningen
      för orders med det märket:
    </p>
    <table>
      <thead><tr><th>Fält</th><th>Beskrivning</th></tr></thead>
      <tbody>
        <tr>
          <td>Bilmärke</td>
          <td>Vilket bilmärke rabatten gäller</td>
        </tr>
        <tr>
          <td>VSN% (Verkstadssäljrabatt)</td>
          <td>Rabatt i procent för verkstäder (Skadeverkstad). T.ex. <span class="mono">15.00%</span></td>
        </tr>
        <tr>
          <td>SPN% (Butiksprisrabatt)</td>
          <td>Rabatt i procent för butiker (AD Butik). T.ex. <span class="mono">12.50%</span></td>
        </tr>
      </tbody>
    </table>

    <div class="info-box">
      <strong>Automatisk prisberäkning:</strong> När ett bilmärke väljs på en order hämtas märkesrabatten
      automatiskt. Systemet beräknar sedan <em>verkstadspriset</em> och <em>butikspriset</em> per artikel
      utifrån listpriset och rabatterna — ingen manuell beräkning behövs.
    </div>

    <h3 class="subsection">Artikelgruppsrabatter</h3>
    <p>
      Artikelgruppsrabatter kan hanteras från användarmenyn under <strong>Artikelgrupp-rabatt</strong>.
      Varje post har namn, beskrivning, artikelgruppens externa ID och en rabattprocent. Poster kan
      listas, visas, skapas och redigeras.
    </p>

    <h3 class="subsection">Artikelrabatter</h3>
    <p>
      Artikelrabatter hanteras under <strong>Artikel-rabatt</strong> i användarmenyn. En rabatt kan
      kopplas till ett artikel-ID (ADID) och innehåller namn, beskrivning och rabattprocent; ett
      fast försäljningspris kan också anges. Poster kan listas, visas, skapas och redigeras.
    </p>
  </div>


  <!-- 9. ADMIN -->
  <div class="section page-break" id="admin">
    <h2 class="section-title">9. Administratörsfunktioner</h2>
    <p>
      Admin-sektionen nås via användarmenyn (ditt namn uppe till höger → "Admin").
      Admin-undermenyn innehåller Orderhantering, FAQ, Inställningar och Synka status. Orderstatusar och artikelmallar har separata administrationsvyer men visas inte som flikar i denna undermeny.
    </p>

    <h3 class="subsection">9.1 Orderhantering (Admin)</h3>
    <p>
      Möjlighet att söka bland <strong>alla orders oavsett status</strong>. Viktigaste funktionen
      är att <strong>ångra export</strong> av en order:
    </p>
    <ul>
      <li>Sök upp ordern med ordernummer, registreringsnummer eller annan information.</li>
      <li>Klicka "Ångra export" för att ta bort exportmarkeringen och möjliggöra ny export.</li>
    </ul>
    <div class="warn-box">
      Att ångra export är en känslig åtgärd. Det innebär att ordern i AX kan ha en avvikelse.
      Koordinera med AX/ekonomi-teamet vid behov.
    </div>

    <h3 class="subsection">9.2 FAQ</h3>
    <p>
      Statisk informationssida för handläggare och administratörer. Innehåller information om:
    </p>
    <ul>
      <li>Regler för AX-leverantörssynkronisering</li>
      <li>Tidssättning för export</li>
      <li>Vanliga frågor och svar om systemet</li>
    </ul>

    <h3 class="subsection">9.3 Inställningar</h3>
    <p>
      Konfiguration av <strong>standardrabatter per ordertyp</strong> för den aktiva klienttypen.
      Varje ordertyp (t.ex. "Garanti", "Skadeärende") kan ha ett förinställt rabattvärde i procent.
      Ändringarna sparas direkt till API:et.
    </p>

    <h3 class="subsection">9.4 Synka orderstatus</h3>
    <p>
      Under fliken <strong>Synka status</strong> kan en administratör starta en manuell kontroll av orders
      med status <em>Inköp</em>. ADOne kontrollerar statusen i AX via FO och ändrar en order till
      <em>Leverans</em> om den är utlevererad. En automatisk synk körs även varje natt. Funktionen visar
      ett felmeddelande om synkroniseringen misslyckas.
    </p>

    <h3 class="subsection">9.5 Orderstatusar (Admin)</h3>
    <div class="warn-box">
      <strong>Separat administrationsvy:</strong> Orderstatusar är inte en flik i Admin-undermenyn.
    </div>
    <p>
      Fullständig administration av orderstatusar &mdash; se avsnitt 5 för detaljbeskrivning.
      Härifrån kan man:
    </p>
    <ul>
      <li>Se alla statusar med sorteringsordning och klienttyp</li>
      <li>Skapa ny orderstatus</li>
      <li>Redigera befintlig status (alla konfigurationsfält)</li>
      <li>Se detaljer om en specifik status</li>
    </ul>

    <h3 class="subsection">9.6 Artikelmallar (Admin)</h3>
    <div class="warn-box">
      <strong>Separat administrationsvy:</strong> Artikelmallar är inte en flik i Admin-undermenyn.
    </div>
    <p>
      Se avsnitt 10 nedan.
    </p>
  </div>


  <!-- 10. ARTICLE TEMPLATES -->
  <div class="section" id="articletemplate">
    <h2 class="section-title">10. Artikelmallar</h2>
    <div class="warn-box">
      <strong>Gäller enbart Fleet-orders:</strong> Artikelmallar är bara tillgängliga när klienttypen är inställd på <span class="badge badge-green">Fleet</span>. Funktionen visas inte för Skadeverkstad eller AD Butik.
    </div>
    <p>
      Artikelmallar är förinställda artiklar som kan laddas in snabbt när man lägger till artikelrader
      på en Fleet-order. Det sparar tid och minskar risken för felinmatning.
    </p>

    <h3 class="subsection">Fält per artikelmall</h3>
    <table>
      <thead><tr><th>Fält</th><th>Beskrivning</th></tr></thead>
      <tbody>
        <tr><td>Artikelnummer</td><td>Unikt nummer som identifierar artikeln</td></tr>
        <tr><td>Beskrivning</td><td>Textbeskrivning av artikeln</td></tr>
        <tr><td>Listpris</td><td>Standardpris (används om "Behåll inkommande pris" är av)</td></tr>
        <tr><td>Behåll inkommande pris</td><td>Om aktiverad används det pris som anges manuellt vid orderläggning</td></tr>
        <tr><td>Artikeltyp</td><td><span class="badge badge-blue">Produkt</span> eller <span class="badge badge-orange">Tid</span></td></tr>
      </tbody>
    </table>

    <div class="info-box">
      <strong>Artikeltyper:</strong> <em>Produkt</em> avser fysiska reservdelar. <em>Tid</em> avser arbetstid
      (t.ex. montering). Båda typerna kan läggas till på samma order. Mallar filtreras på typ
      för snabbare val i orderformuläret.
    </div>

    <h3 class="subsection">Funktioner</h3>
    <ul>
      <li><strong>Lista mallar:</strong> Visar artikelnummer, beskrivning, pris och typ.</li>
      <li><strong>Skapa mall:</strong> Nytt formulär.</li>
      <li><strong>Redigera mall:</strong> Uppdatera befintlig.</li>
      <li><strong>Visa mall:</strong> Läsvy med alla fält.</li>
    </ul>
  </div>


  <!-- 11. DASHBOARD -->
  <div class="section" id="dashboard">
    <h2 class="section-title">11. Dashboard &mdash; startsida</h2>
    <p>
      Dashboarden är startsidan som visas direkt efter inloggning. Den ger en snabb överblick
      och tillgång till de viktigaste funktionerna.
    </p>

    <h3 class="subsection">Innehavande delar</h3>
    <table>
      <thead><tr><th>Del</th><th>Beskrivning</th></tr></thead>
      <tbody>
        <tr>
          <td><strong>Mina tilldelade orders</strong></td>
          <td>
            En tabell med orders som är tilldelade till den inloggade användaren &mdash; oavsett status.
            Klicka på en order för att gå direkt till ordervyn.
          </td>
        </tr>
        <tr>
          <td><strong>Inkommande orders &mdash; statistik</strong></td>
          <td>
            Räknare som visar antal inkommande orders per dag (för aktiv klienttyp).
            Uppdateras vid sidladdning.
          </td>
        </tr>
        <tr>
          <td><strong>Global ordersökning</strong></td>
          <td>
            Sök bland <em>alla orders</em> oavsett status. Stödjer text-sökning med
            sidnumrering (50 orders per sida). Klicka på en order för att öppna den.
          </td>
        </tr>
        <tr>
          <td><strong>Exportera orders (CSV)</strong></td>
          <td>
            Välj datumintervall och ladda ner en CSV-fil med alla orders under perioden.
            Används för rapportering och uppföljning.
          </td>
        </tr>
      </tbody>
    </table>
  </div>


  <!-- 12. PRICING -->
  <div class="section" id="pricing">
    <h2 class="section-title">12. Prisberäkningslogik</h2>
    <p>
      Nedan beskrivs hur systemet beräknar priser per artikelrad. Förståelse för detta
      är viktig för att se till att rätt priser hamnar i orders.
    </p>

    <h3 class="subsection">Tre priskolumner per artikelrad</h3>
    <table>
      <thead><tr><th>Kolumn</th><th>Beskrivning</th></tr></thead>
      <tbody>
        <tr>
          <td><strong>Listpris (Säljpris)</strong></td>
          <td>Grundpris utan rabatt. Matas in manuellt eller hämtas från artikelmall.</td>
        </tr>
        <tr>
          <td><strong>VSN-pris (Verkstadssäljpris)</strong></td>
          <td>Pris för skadeverkstäder = Listpris × (1 − VSN%)</td>
        </tr>
        <tr>
          <td><strong>SPN-pris (Butikssäljpris)</strong></td>
          <td>Pris för AD Butiker = Listpris × (1 − SPN%)</td>
        </tr>
      </tbody>
    </table>

    <h3 class="subsection">Automatisk beräkning</h3>
    <p>
      När ett <strong>bilmärke</strong> väljs på ordern:
    </p>
    <ol>
      <li>Systemet hämtar märkesrabatten (VSN% och SPN%) för det märket.</li>
      <li>För varje artikel beräknas VSN- och SPN-priserna automatiskt ur listpriset och rabatten.</li>
      <li>Användaren kan manuellt skriva in ett listpris istället, och priserna räknas om.</li>
    </ol>

    <h3 class="subsection">Prisformat i systemet</h3>
    <div class="info-box">
      <strong>OBS:</strong> Priser lagras internt i systemet och i APIet som <em>heltal i öresklass</em>
      (t.ex. <span class="mono">1250</span> = 12,50 kr). Systemet konverterar automatiskt till och från
      visningsformat. Användaren ser alltid decimala kronor (t.ex. <span class="mono">12.50</span>).
    </div>

    <p>Rabatter lagras som heltal i <em>baspunkter</em> (t.ex. <span class="mono">1500</span> = 15,00%).</p>
  </div>


  <!-- 13. ENVIRONMENTS -->
  <div class="section" id="environments">
    <h2 class="section-title">13. Miljöer &mdash; utveckling, test och produktion</h2>
    <p>Systemet har tre miljöer:</p>
    <table>
      <thead><tr><th>Miljö</th><th>Frontend-URL</th><th>Syfte</th></tr></thead>
      <tbody>
        <tr>
          <td><span class="badge badge-gray">Lokal dev</span></td>
          <td><span class="mono">https://localhost:3000</span></td>
          <td>Utvecklarens lokala maskin</td>
        </tr>
        <tr>
          <td><span class="badge badge-orange">Azure Dev</span></td>
          <td><span class="mono">adonlineappdev.azurewebsites.net</span></td>
          <td>Testmiljö i Azure</td>
        </tr>
        <tr>
          <td><span class="badge badge-green">Produktion</span></td>
          <td><span class="mono">adone.se</span></td>
          <td>Livemiljö, används av handläggare</td>
        </tr>
      </tbody>
    </table>
    <p>
      CI/CD (automatisk bygg- och driftsättningspipeline) är konfigurerad med Azure DevOps-pipelines
      för att automatiskt bygga och driftsätta nya versioner av applikationen.
    </p>
  </div>


  <!-- 14. API & AX -->
  <div class="section" id="api">
    <h2 class="section-title">14. Koppling till API och Microsoft Dynamics AX</h2>

    <h3 class="subsection">Backend REST API</h3>
    <p>
      Alla data i systemet läses från och skrivs till ett <strong>separat backend-API</strong> (.NET).
      Frontenden kommunicerar med API:et via HTTPS.
    </p>
  

    <h3 class="subsection">Koppling till Microsoft Dynamics AX</h3>
    <p>
      AX (ERP-systemet) är inte direkt åtkomligt från frontenden &mdash; allt går via backend-API:et.
      API:et och projektet AX Communication utbyter <strong>JSON-meddelanden via Azure Service Bus</strong>.
      AX Communication sköter filutbytet med AX via SFTP. AX-svar hämtas genom periodisk kontroll av svarssökvägen:
    </p>
    <table>
      <thead><tr><th>Funktion</th><th>Hur det fungerar</th></tr></thead>
      <tbody>
        <tr>
          <td><strong>Export av order</strong></td>
          <td>API:et skickar ordern som JSON till AX Communications orderkö. AX Communication mappar ordern till CSV och lägger filerna på SFTP för AX. OrderResultWorkerService kontrollerar SFTP-svarssökvägen var tionde sekund, mappar AX-svar från TXT-filer till JSON och publicerar dem till API:ets svarskö. API:ets OrderWorkerService lyssnar via AXCommunicatorConfiguration.OrderReciever, deserialiserar ImportOrder och matchar order/artiklar med ADOne-GUID i ExternalId. Orderns SalesOrderNumber (FO) och AX-callbackstatus uppdateras. Artikelradernas PurchaseOrderNumber (IO) och SalesOrderNumber (FO) skrivs bara om fälten är tomma.</td>
        </tr>
        <tr>
          <td><strong>Import av leverantörer</strong></td>
          <td>API:et triggar import via Service Bus. AX Communication hämtar leverantörsfil från AX via SFTP och skickar leverantörslistan som ett samlat JSON-meddelande till API:ets supplier-consumer, som uppdaterar leverantörsregistret.</td>
        </tr>
      </tbody>
    </table>

    <h3 class="subsection">Detaljerad exportdokumentation</h3>
    <p>
      Exportflödet till AX är ett av de mest komplexa flödena i systemet och har sin egen, detaljerade
      dokumentationssida som täcker alla scenarier, felfall, JSON-meddelandets data och konfigurationskrav:
    </p>
    <p>
      <a href="/admin/documentation-export" target="_top" style="display:inline-block;background:#1a3a5c;color:#fff;padding:9px 20px;border-radius:5px;text-decoration:none;font-weight:600;font-size:0.92em;">
        📄 Öppna exportflödesdokumentation →
      </a>
    </p>

    <h3 class="subsection">Säkerhet</h3>
    <ul>
      <li>All kommunikation mellan frontend och API sker över HTTPS.</li>
      <li>Varje API-anrop autentiseras med en kortlivad <em>Bearer token</em> från Azure AD.</li>
      <li>Tokens hämtas automatiskt och tyst i bakgrunden &mdash; användaren behöver inte logga in om.</li>
      <li>Backend-APIet validerar alltid roll (Read/Write/Admin) innan åtgärden utförs.</li>
    </ul>
  </div>


  <!-- 15. EXTERNAL INTEGRATIONS -->
  <div class="section" id="integrations">
    <h2 class="section-title">15. Externa integrationer</h2>
    <p>
      Utöver Microsoft Dynamics AX har systemet flera andra externa integrationer som körs
      via backend-API:et och är osynliga för slutanvändaren.
    </p>

    <h3 class="subsection">15.1 Katalog-API (AD Butik &mdash; godkännandeflöde)</h3>
    <p>
      För klienttypen <strong>AD Butik</strong> finns en integration mot ett externt katalog-API
      som hanterar ordergodkännanden. Flödet ser ut så här:
    </p>
    <div class="flow">
      <div class="flow-step">Order skickas till Katalog-API</div>
      <div class="flow-arrow">→</div>
      <div class="flow-step">Katalog-systemet beslutar</div>
      <div class="flow-arrow">→</div>
      <div class="flow-step">Callback: Godkänd eller Nekad</div>
      <div class="flow-arrow">→</div>
      <div class="flow-step">Orderstatus uppdateras automatiskt</div>
    </div>
    <ul>
      <li>Godkända orders sätts automatiskt till konfigurerad &ldquo;katalog-godkänd status&rdquo;.</li>
      <li>Nekade orders sätts till &ldquo;katalog-nekad status&rdquo;.</li>
      <li>Åtkomst till detta flöde kräver särskild behörighet (<em>CatalogAuthorize</em>).</li>
    </ul>

    <h3 class="subsection">15.2 ADI API (artikelinformation från reservdelsleverantör)</h3>
    <p>
      Systemet kan hämta artikelinformation från en extern reservdelsleverantör (ADI):
    </p>
    <ul>
      <li>Slå upp artikeldetaljer</li>
      <li>Kontrollera lagerstatus i realtid</li>
      <li>Hämta aktuella priser</li>
      <li>Verifiera delkompatibilitet för ett specifikt fordon</li>
    </ul>

    <h3 class="subsection">15.3 E-post (SMTP)</h3>
    <p>
      Backend-API:et hanterar all e-postsändning via SMTP. E-post skickas vid:
    </p>
    <ul>
      <li>Prisförslag till PostNord (Fleet-orders, triggas manuellt av handläggare)</li>
      <li>Orderbekräftelser och statusuppdateringar</li>
    </ul>
    <p>
      Alla skickade e-postmeddelanden loggas i systemet och är synliga i ordervyn under
      &ldquo;E-posthistorik&rdquo;.
    </p>

    <h3 class="subsection">15.4 SharePoint (dokumentlagring)</h3>
    <p>
      Systemet har en integration mot SharePoint för dokumentlagring. Dokument kan
      organiseras i mappstrukturer och delas med relevanta intressenter.
    </p>

    <h3 class="subsection">Integrationsöversikt</h3>
    <table>
      <thead><tr><th>Integration</th><th>Protokoll</th><th>Syfte</th></tr></thead>
      <tbody>
        <tr><td>ADOne API ↔ AX Communication</td><td>Azure Service Bus (JSON-meddelanden)</td><td>Orderexport, svar från AX och leverantörsimport</td></tr>
        <tr><td>AX Communication ↔ Microsoft Dynamics AX</td><td>SFTP (CSV-orderfiler och TXT-svarsfiler)</td><td>Orderutbyte och svarspollning var 10:e sekund</td></tr>
        <tr><td>Katalog-API</td><td>HTTPS / Callbacks</td><td>Ordergodkännande för AD Butik</td></tr>
        <tr><td>ADI API</td><td>HTTPS</td><td>Artikelinfo, lager, priser, kompatibilitet</td></tr>
        <tr><td>E-post (SMTP)</td><td>SMTP</td><td>Prisförslag, orderbekräftelser</td></tr>
        <tr><td>SharePoint</td><td>HTTPS</td><td>Dokumentlagring och delning</td></tr>
      </tbody>
    </table>
  </div>


  <!-- 16. DATA & SECURITY -->
  <div class="section" id="datalagring">
    <h2 class="section-title">16. Datalagring och säkerhetsaspekter</h2>

    <h3 class="subsection">Databas och databevarelse</h3>
    <p>
      ADOnes order- och registerdata hanteras via backend-API:ets SQL Server-databas. AX Communication
      har dessutom en separat integrationsdatabas för inkommande order och behandling av AX-svar.
    </p>
    <div class="info-box">
      <strong>Mjuk radering i ADOne:</strong> ADOne-poster som tas bort markeras enligt API-dokumentationen
      som borttagna (<span class="mono">IsDeleted = true</span>) i stället för att raderas permanent.
      Detta ska inte tolkas som en generell regel för AX Communications separata integrationsdatabas.
    </div>
    <div class="warn-box">
      AX Communication tar bort sin behandlade orderpost ur integrationsdatabasen efter att AX-svaret hanterats.
      SFTP-svarsfiler arkiveras inte automatiskt. Dessa beteenden gäller integrationsprojektet, inte ADOne-orderdatabasen.
    </div>

    <h3 class="subsection">Ändringshistorik och spårbarhet</h3>
    <p>Systemet loggar alla viktiga händelser automatiskt:</p>
    <table>
      <thead><tr><th>Vad loggas</th><th>Var syns det</th></tr></thead>
      <tbody>
        <tr><td>Alla ändringar på en order (vem, vad, när)</td><td>Orderhistorik-accordion i ordervyn</td></tr>
        <tr><td>Alla ändringar på en leverantör</td><td>Leverantörshistorik-accordion</td></tr>
        <tr><td>Alla skickade e-postmeddelanden</td><td>E-posthistorik-accordion i ordervyn</td></tr>
      </tbody>
    </table>

    <h3 class="subsection">Felhantering &mdash; vad händer när något går fel</h3>
    <p>
      Backend-API:et returnerar standardiserade felmeddelanden. Frontenden visar dessa
      för användaren som varningar eller felmeddelanden. Vanliga felkoder:
    </p>
    <table>
      <thead><tr><th>HTTP-kod</th><th>Betydelse</th><th>Exempel</th></tr></thead>
      <tbody>
        <tr><td><span class="badge badge-orange">400</span></td><td>Felaktig begäran / valideringsfel</td><td>Obligatoriskt fält saknas</td></tr>
        <tr><td><span class="badge badge-orange">403</span></td><td>Åtkomst nekad</td><td>Fel roll för åtgärden</td></tr>
        <tr><td><span class="badge badge-gray">404</span></td><td>Hittades inte</td><td>Order-ID finns inte</td></tr>
        <tr><td><span class="badge badge-orange">409</span></td><td>Konflikt / dublett</td><td>PostNord ID finns redan på en annan order</td></tr>
        <tr><td><span class="badge badge-orange">422</span></td><td>Kan ej bearbetas</td><td>Order uppfyller inte exportkraven</td></tr>
        <tr><td><span class="badge badge-blue">500</span></td><td>Serverfel</td><td>Export till AX misslyckades</td></tr>
      </tbody>
    </table>

    <h3 class="subsection">Övervakning</h3>
    <p>
      Systemet är anslutet till <strong>Azure Monitor / Application Insights</strong>
      vilket innebär att driftproblem, fel och prestanda loggas automatiskt och kan analyseras
      av driftteamet. Distribuerad spårning via OpenTelemetry gör det möjligt att följa
      ett anrop hela vägen från frontend till databas och externa system.
    </p>
  </div>


  <!-- 17. BACKEND ARCHITECTURE -->
  <div class="section" id="backend">
    <h2 class="section-title">17. Backend-arkitektur (teknisk översikt)</h2>
    <p>
      För produktägare och projektledare som vill förstå hur backenden är uppbyggd på en övergripande nivå.
    </p>

    <h3 class="subsection">Projektstruktur</h3>
    <table>
      <thead><tr><th>Projekt</th><th>Roll</th></tr></thead>
      <tbody>
        <tr><td><strong>ADonlineappAPI</strong></td><td>Huvud-API-projekt med controllers, services, DTOs och middleware (.NET 9)</td></tr>
        <tr><td><strong>ADonlineappAPI.Domain</strong></td><td>Domänlagret: entiteter, enums, repositoryinterfaces</td></tr>
        <tr><td><strong>ADonlineappAPI.Persistence</strong></td><td>Dataåtkomst: Entity Framework Core, DbContext, migrationer</td></tr>
        <tr><td><strong>ADonlineappAPI.AXCommunicator</strong></td><td>Azure Service Bus-kommunikation mellan API:et och AX Communication via JSON-meddelanden</td></tr>
        <tr><td><strong>AX Communication</strong></td><td>Separat integrationsprojekt som tar emot JSON via Service Bus, mappar order till CSV och hanterar SFTP-utbytet med AX. Pollar svarsfiler var 10:e sekund och skickar mappade svar till Service Bus.</td></tr>
        <tr><td><strong>Tests</strong></td><td>Integrationstester och testhjälpare</td></tr>
      </tbody>
    </table>

    <p>
      I AX Communication lyssnar <strong>OrderWorkerService</strong> och <strong>SupplierWorkerService</strong> på inkommande köer.
      <strong>OrderResultWorkerService</strong> pollar AX:s SFTP-svarssökväg var tionde sekund och publicerar behandlade svar
      på API:ets svarskö. I API:t tar <strong>OrderWorkerService</strong> emot AX-svar och
      <strong>SupplierWorkerService</strong> tar emot leverantörslistor från respektive konfigurerad kö.
    </p>

    <h3 class="subsection">Dataflöde (förenklat)</h3>
    <div class="flow">
      <div class="flow-step">Frontend</div>
      <div class="flow-arrow">→</div>
      <div class="flow-step">API Controller (v1/v2)</div>
      <div class="flow-arrow">→</div>
      <div class="flow-step">Azure AD-autentisering</div>
      <div class="flow-arrow">→</div>
      <div class="flow-step">Affärslogik (Service)</div>
      <div class="flow-arrow">→</div>
      <div class="flow-step">SQL Server-databas</div>
    </div>

    <h3 class="subsection">Nyckelprinciper</h3>
    <ul>
      <li><strong>Repository Pattern:</strong> All databaskommunikation sker via väldefinierade interfaces &mdash; enklare att testa och byta ut.</li>
      <li><strong>Versionshantering:</strong> API:et har v1 (legacy) och v2 (aktuell). Ny funktionalitet byggs i v2.</li>
      <li><strong>DTO-mönster:</strong> Frontenden ser aldrig rå databasdata, utan alltid formaterade "response models".</li>
      <li><strong>Transaktioner:</strong> Unit of Work-mönstret säkerställer att relaterade ändringar sparas atomärt.</li>
    </ul>
  </div>

</div><!-- end .container -->

<div class="doc-footer">
  ADOne &mdash; Systemdokumentation v1.3 &bull; AD Sverige &bull; September 2026<br>
</div>

<script>
  document.addEventListener('click', function (event) {
    var link = event.target.closest('a[href^="#"]');
    if (!link) return;

    var target = document.getElementById(link.getAttribute('href').slice(1));
    if (!target) return;

    event.preventDefault();
    target.scrollIntoView({ behavior: 'smooth', block: 'start' });
  });
</script>

</body>
</html>
`;

const AdminDocumentation: FC = () => (
<iframe
  className="documentation-frame"
  title="ADOne systemdokumentation"
  srcDoc={documentationHtml}
  style={{
    width: "100%",
    height: "calc(100vh - 120px)",
    border: 0,
    display: "block",
  }}
/>
);

export default AdminDocumentation;
